import React, { useState, useCallback, memo, useEffect } from 'react';
import Lottie from 'lottie-react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { setLanguage } from '../../store/slices/languageSlice';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../ThemeProvider';

const DEFAULT_PRIMARY_COLOR = '#3A9355';

const LANGUAGES = [
  { 
    code: 'tr', 
    label: 'Türkçe', 
    flag: 'https://flagcdn.com/w40/tr.png',
    flagAlt: 'Türk Bayrağı'
  },
  { 
    code: 'en', 
    label: 'English', 
    flag: 'https://flagcdn.com/w40/gb.png',
    flagAlt: 'British Flag'
  }
];

// Public paths for lottie animations
const LOTTIE_FILES = {
  tr: '/images/lottie/tr.json',
  en: '/images/lottie/en.json'
};

const LanguageSwitcher = ({ primaryColor: propPrimaryColor }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => state.language.language);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [lottieData, setLottieData] = useState({});
  
  // Dinamik tema rengini al - prop priority
  const { primaryColor: hookPrimaryColor } = useThemeContext();
  const primaryColor = propPrimaryColor || hookPrimaryColor || DEFAULT_PRIMARY_COLOR;
  
  
  // Sync Redux store with i18next on component mount
  useEffect(() => {
    const i18nextLang = i18n.language || 'tr';
    // If there's a mismatch between i18next and Redux, update Redux
    if (i18nextLang !== currentLanguage) {
      dispatch(setLanguage(i18nextLang));
    }
    
    // Listen for language changes from i18next
    const handleLanguageChanged = (lng) => {
      if (lng !== currentLanguage) {
        dispatch(setLanguage(lng));
      }
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [dispatch, currentLanguage]);

  // Preload lottie animations for available languages
  useEffect(() => {
    let isCancelled = false;
    const loadAll = async () => {
      try {
        const entries = await Promise.all(
          Object.entries(LOTTIE_FILES).map(async ([code, path]) => {
            const res = await fetch(path);
            if (!res.ok) throw new Error(`Failed to load ${path}`);
            const json = await res.json();
            return [code, json];
          })
        );
        if (!isCancelled) setLottieData(Object.fromEntries(entries));
      } catch (e) {
        console.error('Lottie preload error:', e);
      }
    };
    loadAll();
    return () => {
      isCancelled = true;
    };
  }, []);

  const handleLanguageChange = useCallback(async (lang) => {
    try {
      setIsChanging(true);
      await i18n.changeLanguage(lang);
      setDropdownOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChanging(false);
    }
  }, []);

  // Find the current language object
  const currentLang = LANGUAGES.find(lang => lang.code === currentLanguage) || LANGUAGES[0];

  const withOpacity = (color, opacity) => {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  return (
    <div className="relative inline-block text-left">
      <motion.button
        type="button"
        className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm leading-4 font-medium rounded-lg text-white shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50"
        style={{ 
          backgroundColor: primaryColor,
          '--tw-ring-color': primaryColor,
          boxShadow: `0 2px 4px ${withOpacity(primaryColor, 0.2)}`
        }}
        onClick={() => setDropdownOpen(!isDropdownOpen)}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
        disabled={isChanging}
        aria-label={t('common.languageSwitcher')}
        whileHover={{ 
          scale: 1.02,
          boxShadow: `0 4px 6px ${withOpacity(primaryColor, 0.3)}`
        }}
        whileTap={{ 
          scale: 0.98,
          boxShadow: `0 1px 2px ${withOpacity(primaryColor, 0.2)}`
        }}
      >
        <span className="flex items-center gap-3">
          <div className="relative w-6 h-6 flex items-center justify-center overflow-hidden rounded-sm shadow-sm">
            {lottieData[currentLang.code] ? (
              <Lottie
                animationData={lottieData[currentLang.code]}
                loop
                autoplay
                style={{ width: 32, height: 32 }}
              />
            ) : (
              <img
                src={currentLang.flag}
                alt={currentLang.flagAlt}
                className="w-7 h-7 object-cover"
                loading="lazy"
              />
            )}
            <div 
              className="absolute inset-0 rounded-sm pointer-events-none"
              style={{ 
                boxShadow: `inset 0 0 0 1px ${withOpacity(primaryColor, 0.2)}`,
                backgroundColor: withOpacity(primaryColor, 0.05)
              }}
            />
          </div>
          <span className="font-medium">{currentLang.label}</span>
        </span>
      </motion.button>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
            style={{
              boxShadow: `0 4px 6px ${withOpacity(primaryColor, 0.1)}`,
              borderColor: withOpacity(primaryColor, 0.1)
            }}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="language-menu"
          >
            <div className="py-1" role="none">
              {LANGUAGES.map((lang) => (
                <motion.button
                  key={lang.code}
                  className={`${
                    currentLanguage === lang.code
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  } group flex items-center w-full px-4 py-3 text-sm transition-colors duration-200`}
                  role="menuitem"
                  onClick={() => handleLanguageChange(lang.code)}
                  disabled={isChanging || currentLanguage === lang.code}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    backgroundColor: currentLanguage === lang.code 
                      ? withOpacity(primaryColor, 0.1)
                      : 'transparent'
                  }}
                >
                  <div className="relative mr-3 w-7 h-7 flex items-center justify-center overflow-hidden rounded-sm shadow-sm">
                    {lottieData[lang.code] ? (
                      <Lottie
                        animationData={lottieData[lang.code]}
                        loop
                        autoplay
                        style={{ width: 28, height: 28 }}
                      />
                    ) : (
                      <img
                        src={lang.flag}
                        alt={lang.flagAlt}
                        className="w-7 h-7 object-cover"
                        loading="lazy"
                      />
                    )}
                    <div 
                      className="absolute inset-0 rounded-sm pointer-events-none"
                      style={{ 
                        boxShadow: `inset 0 0 0 1px ${withOpacity(primaryColor, 0.2)}`,
                        backgroundColor: withOpacity(primaryColor, 0.05)
                      }}
                    />
                  </div>
                  <span className="font-medium">{lang.label}</span>
                  {currentLanguage === lang.code && (
                    <motion.div
                      className="ml-auto w-1.5 h-1.5 rounded-full"
                      style={{ 
                        backgroundColor: primaryColor,
                        boxShadow: `0 0 0 2px ${withOpacity(primaryColor, 0.2)}`
                      }}
                      layoutId="activeLanguage"
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(LanguageSwitcher);